import { useState } from "react";
import { DownloadSimple, Eye, FileCode, FileText, MicrosoftExcelLogo, Sparkle, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, inr, monthStart, today } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const REPORT_FILTERS = ["Party", "Category", "Payment Collection", "Item", "Invoice Details", "Summary"];
const REPORT_CATALOG = [
  { id: "gstr1", title: "GSTR-1 (Sales)", group: "GST", filters: ["Party", "Invoice Details", "Summary"] },
  { id: "gstr3b", title: "GSTR-3B", group: "GST", filters: ["Summary"] },
  { id: "pnl", title: "Profit and Loss Report", group: "TRANSACTION", filters: ["Category", "Summary"] },
];

function ReportCard({ title, description, onPreview, onDownload, previewData, testIdPrefix }) {
  return (
    <div className="flex flex-col rounded-lg border bg-card p-6 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-sm" data-testid={`${testIdPrefix}-card`}>
      <h2 className="font-heading text-lg font-semibold tracking-tight">{title}</h2>
      <p className="mt-1 flex-1 text-sm text-muted-foreground">{description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={onPreview} data-testid={`${testIdPrefix}-preview-button`}>
          <Eye size={15} className="mr-1" /> Preview
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDownload("json")} data-testid={`${testIdPrefix}-json-button`}>
          <FileCode size={15} className="mr-1" /> JSON
        </Button>
        <Button size="sm" onClick={() => onDownload("xlsx")} data-testid={`${testIdPrefix}-excel-button`}>
          <MicrosoftExcelLogo size={15} className="mr-1" /> Excel
        </Button>
      </div>
      {previewData}
    </div>
  );
}

function Gstr1Summary({ report }) {
  const b2b = report.b2b || [];
  const b2cl = report.b2cl || [];
  const b2cs = report.b2cs || [];
  return (
    <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
      <div className="flex justify-between"><span className="text-muted-foreground">Period</span><span>{report.fp}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">B2B customers</span><span className="tabular-nums">{b2b.length}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">B2B invoices</span><span className="tabular-nums">{b2b.reduce((sum, customer) => sum + (customer.inv || []).length, 0)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">B2CL invoices</span><span className="tabular-nums">{b2cl.reduce((sum, place) => sum + (place.inv || []).length, 0)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">B2C entries</span><span className="tabular-nums">{b2cs.length}</span></div>
      <div className="flex justify-between border-t pt-1 font-semibold"><span>B2C taxable</span><span className="tabular-nums">{inr(b2cs.reduce((sum, entry) => sum + (entry.txval || 0), 0))}</span></div>
    </div>
  );
}

function Gstr3bSummary({ report }) {
  const outward = report.sup_details.osup_det;
  const itc = report.itc_elg.itc_net;
  return (
    <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
      <div className="flex justify-between"><span className="text-muted-foreground">GSTIN</span><span>{report.gstin || "Not set"}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Outward taxable</span><span className="tabular-nums">{inr(outward.txval)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Output tax</span><span className="tabular-nums">{inr(outward.iamt + outward.camt + outward.samt)}</span></div>
      <div className="flex justify-between border-t pt-1 font-semibold"><span>Eligible ITC</span><span className="tabular-nums">{inr(itc.iamt + itc.camt + itc.samt)}</span></div>
    </div>
  );
}

function BranchReports({ data, renderReport, testId }) {
  if (data?.scope !== "all") {
    return <div className="mt-4" data-testid={testId}>{renderReport(data)}</div>;
  }
  return (
    <div className="mt-4 space-y-3" data-testid={testId}>
      {data.branches.map((branch) => (
        <section key={branch.branch_id} className="space-y-2 rounded-md border p-3" data-testid={`${testId}-${branch.branch_id}`}>
          <div>
            <h3 className="font-semibold">{branch.branch_name}</h3>
            <p className="text-xs text-muted-foreground">{branch.gstin || "GSTIN not set"}</p>
          </div>
          {renderReport(branch.report)}
        </section>
      ))}
    </div>
  );
}

function downloadFilename(response, fallback) {
  const value = response.headers["content-disposition"] || "";
  const match = value.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

export default function Reports() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [gstr1, setGstr1] = useState(null);
  const [gstr3b, setGstr3b] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [busy, setBusy] = useState("");
  const [scopeDialog, setScopeDialog] = useState(null);
  const [branchScope, setBranchScope] = useState("");
  const [reportFilter, setReportFilter] = useState("");
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem("comfy-ledger-report-favorites") || '["pnl"]');
      return Array.isArray(stored) ? stored.filter((id) => REPORT_CATALOG.some((report) => report.id === id)) : ["pnl"];
    } catch {
      return ["pnl"];
    }
  });

  const loadPnl = async () => {
    setBusy("pnl");
    try {
      const response = await API.get(`/reports/pnl?from=${from}&to=${to}`);
      setPnl(response.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const openScopeDialog = (report, action, format = "preview") => {
    setBranchScope("");
    setScopeDialog({ report, action, format });
  };

  const download = async (report, format, scope = null) => {
    const params = new URLSearchParams({ from, to, format });
    if (scope) params.set("branch_scope", scope);
    const response = await API.get(`/reports/${report}?${params.toString()}`, { responseType: "blob" });
    const fallback = `${report}_${from}_to_${to}.${format === "xlsx" ? "xlsx" : "json"}`;
    const objectUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = downloadFilename(response, fallback);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const confirmScope = async () => {
    if (!scopeDialog || !branchScope) return;
    const { report, action, format } = scopeDialog;
    setScopeDialog(null);
    setBusy(report);
    try {
      if (action === "preview") {
        const params = new URLSearchParams({ from, to, format: "preview", branch_scope: branchScope });
        const response = await API.get(`/reports/${report}?${params.toString()}`);
        if (report === "gstr1") setGstr1(response.data);
        else setGstr3b(response.data);
      } else {
        await download(report, format, branchScope);
      }
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const responseText = await e.response.data.text();
        try {
          const detail = JSON.parse(responseText);
          toast.error(fmtErr({ ...e, response: { ...e.response, data: detail } }));
        } catch {
          toast.error(e.message || "Could not generate the report");
        }
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setBusy("");
    }
  };

  const downloadPnl = async (format) => {
    setBusy("pnl");
    try {
      await download("pnl", format);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const toggleFavorite = (reportId) => {
    setFavorites((current) => {
      const next = current.includes(reportId)
        ? current.filter((id) => id !== reportId)
        : [...current, reportId];
      window.localStorage.setItem("comfy-ledger-report-favorites", JSON.stringify(next));
      return next;
    });
  };

  const openReport = (reportId) => {
    if (reportId === "pnl") loadPnl();
    else openScopeDialog(reportId, "preview");
  };

  const visibleReports = REPORT_CATALOG.filter((report) => !reportFilter || report.filters.includes(reportFilter));
  const renderReportLink = (report, favorite = false) => (
    <div key={`${favorite ? "favorite-" : ""}${report.id}`} className="group flex items-center gap-2 px-4 py-2">
      <button
        type="button"
        className="min-w-0 flex-1 truncate text-left text-sm text-foreground/90 hover:text-primary hover:underline"
        onClick={() => openReport(report.id)}
        data-testid={`report-catalog-${report.id}${favorite ? "-favorite" : ""}`}
      >
        {report.title}
      </button>
      <button
        type="button"
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-amber-500"
        onClick={() => toggleFavorite(report.id)}
        aria-label={`${favorites.includes(report.id) ? "Remove" : "Add"} ${report.title} ${favorites.includes(report.id) ? "from" : "to"} favorites`}
        data-testid={`report-favorite-toggle-${report.id}`}
      >
        <Star size={16} weight={favorites.includes(report.id) ? "fill" : "regular"} className={favorites.includes(report.id) ? "text-amber-500" : ""} />
      </button>
    </div>
  );

  const period = { from, to };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground">Browse and generate your GST and financial reports.</p>
      </div>

      <section className="overflow-hidden rounded-lg border bg-card shadow-sm" data-testid="report-catalog">
        <div className="border-b px-5 py-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <span>Filter By</span>
            {reportFilter && (
              <button type="button" onClick={() => setReportFilter("")} className="ml-auto text-xs text-primary hover:underline" data-testid="report-filter-clear">
                Clear filter
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {REPORT_FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setReportFilter((current) => current === filter ? "" : filter)}
                aria-pressed={reportFilter === filter}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${reportFilter === filter ? "border-primary bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}
                data-testid={`report-filter-${filter.toLowerCase().replaceAll(" ", "-")}`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
        <div className="grid divide-y md:grid-cols-3 md:divide-x md:divide-y-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 bg-muted/50 px-4 py-3 text-xs font-semibold tracking-wide text-muted-foreground">
              <Sparkle size={15} /> FAVOURITE
            </div>
            <div className="min-h-24 py-1">
              {REPORT_CATALOG.filter((report) => favorites.includes(report.id) && (!reportFilter || report.filters.includes(reportFilter))).length
                ? REPORT_CATALOG
                  .filter((report) => favorites.includes(report.id) && (!reportFilter || report.filters.includes(reportFilter)))
                  .map((report) => renderReportLink(report, true))
                : <p className="px-4 py-3 text-xs text-muted-foreground">{reportFilter ? "No favourites match this filter." : "Star a report to pin it here."}</p>}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 bg-muted/50 px-4 py-3 text-xs font-semibold tracking-wide text-muted-foreground">
              <FileText size={15} /> GST
            </div>
            <div className="py-1">
              {visibleReports.filter((report) => report.group === "GST").map((report) => renderReportLink(report))}
              {!visibleReports.some((report) => report.group === "GST") && <p className="px-4 py-3 text-xs text-muted-foreground">No GST reports match this filter.</p>}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 bg-muted/50 px-4 py-3 text-xs font-semibold tracking-wide text-muted-foreground">
              <FileText size={15} /> TRANSACTION
            </div>
            <div className="py-1">
              {visibleReports.filter((report) => report.group === "TRANSACTION").map((report) => renderReportLink(report))}
              {!visibleReports.some((report) => report.group === "TRANSACTION") && <p className="px-4 py-3 text-xs text-muted-foreground">No transaction reports match this filter.</p>}
            </div>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to-input" />
        </div>
        <p className="pb-2 text-xs text-muted-foreground">Period applies to all downloads &amp; previews below.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ReportCard
          title="GSTR-1"
          description="Outward supplies in GSTN schema — B2B, B2CL (inter-state B2C above ₹2.5L), B2CS. JSON matches the GST portal upload schema; Excel matches the official offline-tool workbook."
          onPreview={() => openScopeDialog("gstr1", "preview")}
          onDownload={(format) => openScopeDialog("gstr1", "download", format)}
          testIdPrefix="gstr1"
          previewData={gstr1 && <BranchReports data={gstr1} renderReport={(report) => <Gstr1Summary report={report} />} testId="gstr1-preview" />}
        />
        <ReportCard
          title="GSTR-3B"
          description="Monthly summary — outward tax liability and eligible input tax credit (ITC)."
          onPreview={() => openScopeDialog("gstr3b", "preview")}
          onDownload={(format) => openScopeDialog("gstr3b", "download", format)}
          testIdPrefix="gstr3b"
          previewData={gstr3b && <BranchReports data={gstr3b} renderReport={(report) => <Gstr3bSummary report={report} />} testId="gstr3b-preview" />}
        />
        <ReportCard
          title="Profit &amp; Loss"
          description="Income vs purchases and expenses — gross and net profit for the period."
          onPreview={loadPnl}
          onDownload={downloadPnl}
          testIdPrefix="pnl"
          previewData={
            pnl && (
              <div className="mt-4 space-y-1 rounded-md border bg-muted/40 p-3 text-sm" data-testid="pnl-preview">
                <div className="flex justify-between"><span className="text-muted-foreground">Sales (net of GST)</span><span className="tabular-nums">{inr(pnl.income.sales)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Purchases</span><span className="tabular-nums">{inr(pnl.direct_expenses.purchases)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Expenses</span><span className="tabular-nums">{inr(pnl.indirect_total)}</span></div>
                <div className={`flex justify-between border-t pt-1 font-semibold ${pnl.net_profit < 0 ? "text-[#E02424]" : "text-[#008A52]"}`}>
                  <span>Net Profit</span><span className="tabular-nums">{inr(pnl.net_profit)}</span>
                </div>
              </div>
            )
          }
        />
      </div>

      <div className="rounded-lg border bg-muted/40 p-4 text-xs text-muted-foreground" data-testid="reports-note">
        <DownloadSimple size={14} className="mr-1 inline" />
        JSON exports follow the GSTN schema (fp, b2b, b2cl, b2cs, sup_details, itc_elg) — inter-state B2C invoices above ₹2.5 lakh are reported under B2CL. The GSTR-1 Excel download is the official offline-tool workbook — open it in the GST portal's Returns Offline Tool and import directly.
      </div>

      <Dialog open={!!scopeDialog} onOpenChange={(open) => !open && setScopeDialog(null)}>
        <DialogContent data-testid="gst-report-scope-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Choose GST report branches</DialogTitle>
            <DialogDescription>
              Select the branch scope before {scopeDialog?.action === "preview" ? "previewing" : "generating"} this GST report. Each branch is reported separately with its own GSTIN.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={branchScope} onValueChange={setBranchScope} className="gap-3">
            <label htmlFor="gst-scope-branch" className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
              <RadioGroupItem id="gst-scope-branch" value="branch" className="mt-0.5" />
              <span><span className="block text-sm font-medium">Current branch</span><span className="text-xs text-muted-foreground">Generate only for the branch currently selected.</span></span>
            </label>
            <label htmlFor="gst-scope-all" className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
              <RadioGroupItem id="gst-scope-all" value="all" className="mt-0.5" />
              <span><span className="block text-sm font-medium">All branches</span><span className="text-xs text-muted-foreground">Generate a separate return per branch; downloads are packaged in a ZIP.</span></span>
            </label>
          </RadioGroup>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScopeDialog(null)}>Cancel</Button>
            <Button onClick={confirmScope} disabled={!branchScope || !!busy} data-testid="gst-report-scope-confirm">
              {busy ? "Generating…" : scopeDialog?.action === "preview" ? "Confirm & Preview" : "Confirm & Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
